import * as React from "react";
import {
  Alert,
  Modal as BaseModal,
  Box,
  Button,
  FormHelperText,
  css,
  styled,
} from "@mui/material";
import { grey } from "@mui/material/colors";
import { MuiOtpInput } from "mui-one-time-password-input";
import { Controller, useForm } from "react-hook-form";
import { useDispatch, useSelector } from "react-redux";
import { verifyOtpToVefifyEmail } from "../../redux/auth/authSlice";
import { API_CALL_RES_STATUS } from "../../assets/constants/constants";
import { greenColor, whiteColor } from "../../utils/colors";
import { useNotification } from "../shared/NotificationProvider";

const VerifyEmailModel = ({ open, setOpen }) => {
  const dispatch = useDispatch();
  const verifyEmailRes = useSelector((state) => state.auth.verifyEmailRes);
  const notify = useNotification();

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { otp: "" },
    mode: "onSubmit",
  });

  const handleClose = () => {
    setOpen(false);
    reset();
  };

  const onSubmit = async (data) => {
    try {
      const res = await dispatch(
        verifyOtpToVefifyEmail({ email: verifyEmailRes.data, otp: data.otp })
      );
      if (res.payload.status === API_CALL_RES_STATUS.SUCCESS) {
        notify.success(res.payload.message);
        reset();
        setOpen(false);
      } else {
        notify.error(res.payload.message);
      }
    } catch (error) {
      notify.error(error?.response?.data?.message || "Some system error occurred");
    }
  };

  return (
    <Modal
      aria-labelledby="unstyled-modal-title"
      aria-describedby="unstyled-modal-description"
      open={open}
      onClose={handleClose}
    >
      <ModalContent sx={{ width: 400 }}>
        {verifyEmailRes?.message && (
          <Alert variant="outlined" severity="success">
            {verifyEmailRes.message}
          </Alert>
        )}
        {verifyEmailRes?.data && (
          <h4 id="keep-mounted-modal-title" className="modal-title">
            Verification email is sent on {verifyEmailRes.data}
          </h4>
        )}
        <p id="keep-mounted-modal-description" className="modal-description">
          Please enter the OTP
        </p>

        <form
          onSubmit={(e) => {
            e.stopPropagation();
            return handleSubmit(onSubmit)(e);
          }}
        >
          <Controller
            control={control}
            rules={{ validate: (value) => value.length === 6 }}
            render={({ field, fieldState }) => (
              <Box>
                <MuiOtpInput sx={{ gap: 1 }} {...field} length={6} />
                {fieldState.invalid && (
                  <FormHelperText error>OTP invalid</FormHelperText>
                )}
              </Box>
            )}
            name="otp"
          />
          <Box>
            <Button
              type="submit"
              variant="contained"
              sx={{
                mt: 4,
                float: "right",
                background: greenColor,
                "&:hover": {
                  backgroundColor: whiteColor,
                  color: greenColor,
                },
              }}
            >
              Submit
            </Button>
            <Button
              variant="outlined"
              onClick={handleClose}
              sx={{
                mt: 4,
                float: "left",
                background: whiteColor,
                color: greenColor,
                borderColor: greenColor,
                "&:hover": {
                  backgroundColor: greenColor,
                  color: whiteColor,
                },
              }}
            >
              Close
            </Button>
          </Box>
        </form>
      </ModalContent>
    </Modal>
  );
};

const Modal = styled(BaseModal)(`
  position: fixed;
  z-index: 1300;
  right: 0;
  bottom: 0;
  top: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`);

const ModalContent = styled("div")(
  ({ theme }) => css`
    font-family: "IBM Plex Sans", sans-serif;
    font-weight: 500;
    text-align: start;
    position: relative;
    display: flex;
    flex-direction: column;
    gap: 8px;
    overflow: hidden;
    background-color: ${theme.palette.mode === "dark" ? grey[900] : "#fff"};
    border-radius: 8px;
    border: 1px solid ${theme.palette.mode === "dark" ? grey[700] : grey[200]};
    box-shadow: 0 4px 12px
      ${theme.palette.mode === "dark" ? "rgb(0 0 0 / 0.5)" : "rgb(0 0 0 / 0.2)"};
    padding: 24px;
    color: ${theme.palette.mode === "dark" ? grey[50] : grey[900]};

    & .modal-title {
      margin: 0;
      line-height: 1.5rem;
      margin-bottom: 8px;
    }

    & .modal-description {
      margin: 0;
      line-height: 1.5rem;
      font-weight: 400;
      color: ${theme.palette.mode === "dark" ? grey[400] : grey[800]};
      margin-bottom: 4px;
    }
  `
);

export default VerifyEmailModel;
